import type { SignupErrors } from '../../types/mfc'
import {
    COOKIE_AGE_VERIFICATION,
    COOKIE_PASSCODE,
    COOKIE_USERNAME,
    COOKIE_USER_ID,
    COOKIE_USER_LEVEL,
} from '../constants'
import {
    API_CHANGE_PASSWORD,
    API_LOGIN,
    API_SIGNUP,
    API_SIGNUP_validate,
} from '../constants/endpoints'
import { notifyWorkerOfBrowserReload } from '../state/state.app'
import { controller } from '../utilities/abortController'
import { eraseCookie, getCookie, setCookie } from '../utilities/cookies'
import { utcOffset } from '../utilities/dates'
import { clearSessionStorage } from '../utilities/storage'
import { fetchStatusResolver } from './api/utilities'

export const getUsername = () => getCookie(COOKIE_USERNAME)

export const getPasscode = () => getCookie(COOKIE_PASSCODE)

export const authenticated = () =>
    Boolean(getPasscode()) && Boolean(getUsername())

export const getAgeVerificationCookie = () => getCookie(COOKIE_AGE_VERIFICATION)

export const setAgeVerificationCookie = () => {
    if (typeof window === 'undefined') return
    setCookie(COOKIE_AGE_VERIFICATION, '1')
}

export interface LoginResponse {
    err: 0 | 1
    errmsg?: string
    id: string
    method: 'login'
    responseVer: 1
    result: null
}

export const login = async (username: string, password: string) => {
    if (typeof window === 'undefined') return
    const response = await fetch(API_LOGIN, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            username,
            password,
            tz: utcOffset,
            ss: `${screen.width}x${screen.height}`,
        }),
        signal: controller.signal,
    })
    return (await response.json()) as LoginResponse
}

export const logout = () => {
    if (typeof window === 'undefined') return
    controller.abort()
    clearSessionStorage()
    eraseCookie(COOKIE_PASSCODE)
    eraseCookie(COOKIE_USER_ID)
    eraseCookie(COOKIE_USER_LEVEL)
    notifyWorkerOfBrowserReload()
    document.location.href = document.location.origin
    document.location.reload()
}

export interface SignupPostFields {
    consent: boolean
    username: string
    password: string
    email: string
}

export interface SignupResponse {
    err: number
    errmsg?: SignupErrors
    id: string
    method: 'signup'
    responseVer: number
    result: 'verification_email_sent' | null
}

export async function signupPost(fields: SignupPostFields) {
    if (typeof window === 'undefined') return
    const { consent, email, password, username } = fields
    const tz = utcOffset
    const formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)
    formData.append('confirm_password', password)
    formData.append('email', email)
    formData.append('terms', String(consent))
    formData.append('mode', 'app')
    formData.append('tz', String(tz))
    try {
        const resp = await fetch(API_SIGNUP, {
            method: 'POST',
            credentials: 'include',
            body: formData,
            signal: controller.signal,
        })
        return (await resp.json()) as SignupResponse
    } catch (_ex) {
        const unexpectedErrResponse: SignupResponse = {
            err: 1,
            id: '',
            method: 'signup',
            responseVer: 0,
            result: null,
            errmsg: {
                general: [
                    'An unexpected error has occured. If this error persists, please contact support.',
                ],
            },
        }
        return unexpectedErrResponse
    }
}

export type SignupFields = 'email' | 'username' | 'new_password'

export interface ValidateSignupFields {
    email?: string
    username?: string
    current_password?: string
    new_password?: string
}

export interface ValidateSignupResponse {
    err: number
    errmsg?: {
        username?: Array<string>
        password?: Array<string>
    }
    id: string
    method: 'account/qualifyUserPass'
    responseVer: number
    result: true | null
}

export async function validateSignupPost(
    field: SignupFields,
    fields: ValidateSignupFields,
) {
    const { email, new_password, username } = fields
    if (field === 'email' && !email) {
        throw new Error('missing email')
    }
    if (field === 'email') return
    if (field === 'username' && !username) {
        throw new Error('missing username')
    }
    const password = field === 'new_password'
    if (password && !new_password) {
        throw new Error('missing password')
    }
    const formData = new FormData()
    if (username) formData.append('new_username', username)
    if (password) {
        formData.append('new_password', new_password)
        formData.append('new_password_confirm', new_password)
    }
    const resp = await fetch(API_SIGNUP_validate, {
        method: 'POST',
        credentials: 'include',
        body: formData,
    })
    return (await resp.json()) as ValidateSignupResponse
}

export async function changeUsernamePost(token: string, username: string) {
    const url = 'https://www.myfreecams.com/php/account.php?request=status'
    const formData = new FormData()
    formData.append('new_username', username)
    formData.append('change_username', '1')
    formData.append('token', token)
    return fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        signal: controller.signal,
    }).then(fetchStatusResolver)
}

interface IChangePasswordResponse {
    id?: string
    responseVer?: string
    method?: string
    result?: string // either null or "success"
    err?: number // either 0 (ok) or no problems
    errmsg?: {
        current_password?: string
        desired_password?: string
        errors?: Array<string>
    }
}

export const changePasswordResolver = async (
    response: Response,
    successHandler,
    errorHandler,
) => {
    if (response.status === 200) {
        successHandler([
            'Password changed successfully! Your session will be reloaded shortly.',
        ])
    } else {
        const res: IChangePasswordResponse = await response.json()
        errorHandler(
            res?.errmsg?.errors || [
                'An unknown error occurred. Please try again later or contact support if you see this message repeatedly.',
            ],
        )
    }
}

export async function changePasswordPost(
    token: string,
    currentPassword: string,
    newPassword: string,
) {
    const url = API_CHANGE_PASSWORD
    const formData = new FormData()
    formData.append('current_password', currentPassword)
    formData.append('desired_password', newPassword)
    formData.append('token', token)
    return fetch(url, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        signal: controller.signal,
    })
}
